import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * A dropdown menu that cannot be clipped by whatever it opens inside.
 *
 * Built on the native popover API for the same reason `Modal` is built on
 * `<dialog>`: the top layer, light dismiss, Escape, one-open-at-a-time and
 * returning focus to the invoker are exactly the parts a hand-rolled overlay
 * gets wrong — and there were four hand-rolled copies of this before, each
 * with its own `mousedown` listener and its own guess at a z-index.
 *
 * **z-index was never the problem.** A tile menu already set `z-30` and was
 * still sliced in half, because `ArtifactCard`'s root carries `overflow-hidden`
 * for its rounded corners and an overflow ancestor clips regardless of
 * stacking order. A top-layer element has no clipping ancestor at all, so a
 * 1×1 tile can reach the control that makes it bigger.
 *
 * The popover stays where it is in the React tree rather than being portalled
 * to `document.body`, which keeps two things working for free: events still
 * bubble to the surrounding component, and `.print-page [role="menu"]` still
 * matches it by ancestry, so a print does not grow a floating dropdown.
 *
 * Position is computed rather than declared, because CSS anchor positioning is
 * still Chromium-only. It is recomputed while the menu is open — a dashboard
 * scrolls, and a menu that stays behind is worse than one that is clipped.
 */

export interface MenuTriggerProps {
  popoverTarget: string;
  "aria-haspopup": "menu";
  "aria-expanded": boolean;
}

/** Distance between the trigger and the menu, and from the viewport edge. */
const GAP = 4;
const MARGIN = 8;

export function Menu({
  trigger,
  children,
  className,
  align = "end",
  label,
}: {
  /** The control that opens the menu. Spread the props onto it. */
  trigger: (props: MenuTriggerProps) => ReactNode;
  /** The menu's contents. `close` dismisses it — call it before acting. */
  children: (close: () => void) => ReactNode;
  className?: string;
  align?: "start" | "end";
  label?: string;
}) {
  const id = useId().replace(/:/g, "");
  const holder = useRef<HTMLSpanElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const place = useCallback(() => {
    const anchor = holder.current;
    const menu = surface.current;
    if (!anchor || !menu) return;
    const from = anchor.getBoundingClientRect();
    const width = menu.offsetWidth;
    const height = menu.offsetHeight;

    // Below the trigger, unless the viewport has more room above it — which
    // is the case for a tile near the bottom of a long dashboard.
    const below = window.innerHeight - from.bottom - GAP - MARGIN;
    const flip = height > below && from.top - GAP - MARGIN > below;
    const top = flip ? Math.max(MARGIN, from.top - GAP - height) : from.bottom + GAP;

    const raw = align === "end" ? from.right - width : from.left;
    const left = Math.min(Math.max(MARGIN, raw), Math.max(MARGIN, window.innerWidth - width - MARGIN));

    menu.style.top = `${Math.round(top)}px`;
    menu.style.left = `${Math.round(left)}px`;
    menu.style.maxHeight = `${Math.round(window.innerHeight - top - MARGIN)}px`;
  }, [align]);

  useEffect(() => {
    const menu = surface.current;
    if (!menu) return;
    const toggled = (event: Event) => {
      const next = (event as ToggleEvent).newState === "open";
      setOpen(next);
      if (next) place();
    };
    menu.addEventListener("toggle", toggled);
    return () => menu.removeEventListener("toggle", toggled);
  }, [place]);

  // Follow the trigger for as long as the menu is open, by watching where it
  // actually is rather than by guessing which events move it. Scrolling is the
  // obvious case; the one that caught this out is resizing a tile *from this
  // menu* — the grid reflows, the trigger moves, and a menu positioned once at
  // open time is left pointing at the tile that took its place.
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    let last = "";
    const follow = () => {
      const box = holder.current?.getBoundingClientRect();
      const key = box ? `${box.top},${box.left},${box.right}` : "";
      if (key !== last) {
        last = key;
        place();
      }
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(frame);
  }, [open, place]);

  const close = useCallback(() => surface.current?.hidePopover(), []);

  return (
    <span ref={holder} className="inline-flex">
      {trigger({ popoverTarget: id, "aria-haspopup": "menu", "aria-expanded": open })}
      <div
        ref={surface}
        id={id}
        popover="auto"
        role="menu"
        aria-label={label}
        // The UA stylesheet centres a popover with `inset: 0; margin: auto`,
        // so both are cleared before the computed position is applied.
        style={{ position: "fixed", inset: "auto", margin: 0 }}
        className={cn(
          "bg-popover border-border rounded-element shadow-popover",
          // `display` is the one property a popover's own classes must not set
          // unconditionally: the UA hides a closed popover with `display: none`
          // and any author rule wins, so a plain `flex` here leaves all four
          // menus permanently on screen.
          "[&:popover-open]:flex flex-col overflow-auto border p-1",
          className,
        )}
      >
        {children(close)}
      </div>
    </span>
  );
}
